/** Lightweight performance helpers for dev-time baseline/delta tracking. */

type PerfMetric = {
  name: string;
  duration: number;
  timestamp: number;
};

declare global {
  interface Window {
    __TKR_PERF_METRICS__?: PerfMetric[];
  }
}

function isPerfEnabled(): boolean {
  return import.meta.env.MODE !== 'production' && typeof performance !== 'undefined';
}

export function perfMark(markName: string): void {
  if (!isPerfEnabled()) return;
  performance.mark(markName);
}

export function perfMeasure(
  metricName: string,
  startMark: string,
  endMark: string,
  options?: { clear?: boolean }
): number | null {
  if (!isPerfEnabled()) return null;
  try {
    performance.measure(metricName, startMark, endMark);
    const entries = performance.getEntriesByName(metricName, 'measure');
    const latest = entries[entries.length - 1];
    if (!latest) {
      return null;
    }

    const duration = latest.duration;
    const metrics = window.__TKR_PERF_METRICS__ ?? [];
    metrics.push({
      name: metricName,
      duration,
      timestamp: Date.now(),
    });
    window.__TKR_PERF_METRICS__ = metrics;
    console.debug(`[perf] ${metricName}: ${duration.toFixed(2)}ms`);
    return duration;
  } catch {
    return null;
  } finally {
    if (options?.clear ?? true) {
      performance.clearMarks(startMark);
      performance.clearMarks(endMark);
      performance.clearMeasures(metricName);
    }
  }
}
